import { Component, computed, effect, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { BaseDestroyable } from '@core/base-destroyable';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { TimberService } from '@logging/timber.service';
import { AssignmentStatus } from '@shared/enums';
import { OrdersService } from 'app/doctors/orders/orders.service';
import { RxService } from 'app/rx/services/rx.service';
import { map, tap } from 'rxjs/operators';
import { firstValueFrom, Observable } from 'rxjs';
import { BiAction, BiObjectNames, BiObjectType, BiSeverity, EvenlyCloneStage, Pages } from '@logging/interfaces/bi.enums';

@Component({
	selector: 'assign-to-me-button',
	templateUrl: './assign-to-me-button.component.html',
	styleUrls: ['./assign-to-me-button.component.scss'],
})
export class AssignToMeButtonComponent extends BaseDestroyable implements OnInit, OnDestroy {
	@Input()
	buttonId: string;

	@Input()
	orderId: string;

	@Output() cloneCompleted = new EventEmitter<boolean>();

	assignmentStatus = signal<number>(AssignmentStatus.UnAssigned);

	loadingSpinner = computed(() => {
		const status = this.assignmentStatus();
		if (status == AssignmentStatus.Assigning) {
			return true;
		} else {
			return false;
		}
	});

	showAssignToMe = computed(() => {
		const status = this.assignmentStatus();
		if (status != AssignmentStatus.Assigned) {
			return true;
		} else {
			return false;
		}
	});

	constructor(
		private readonly timberService: TimberService,
		private readonly globalSettings: GlobalSettingsService,
		private readonly orderService: OrdersService,
		private readonly rxService: RxService
	) {
		super();
		effect(() => {
			const status = this.assignmentStatus();
			if (status == AssignmentStatus.Assigned) {
				this.cloneCompleted.emit(true);
			}
		});
	}

	async ngOnInit() : Promise<void> {
		await firstValueFrom(this.getRxAssignmentStatusAndUpdateButton(true));
	}

	ngOnDestroy() : void {
		super.ngOnDestroy();
	}

	async assignToMe(orderId: string) : Promise<void> {
		this.assignmentStatus.set(AssignmentStatus.Assigning);
		this.timberService.userActionEvent({
			locationParentObject: Pages.Orders,
			objectName: BiObjectNames.AssignCaseToMe,
			objectType: BiObjectType.Button,
			action: BiAction.Clicked,
			selectedValue: orderId,
			automationId: "",
		});
		this.timberService.stageReportEvent({
			object: BiObjectNames.AssignCaseToMe,
			description: `${orderId}`,
			stage: EvenlyCloneStage.Started
		});
		await firstValueFrom(this.orderService.assignOrderToDoctor(orderId, this.globalSettings.get().contactId))
			.then(async () => {
				this.assignmentStatus.set(AssignmentStatus.Assigned);
				this.timberService.stageReportEvent({
					object: BiObjectNames.AssignCaseToMe,
					description: `${orderId}`,
					stage: EvenlyCloneStage.Successful,
				});
				this.timberService.info('Order assigned successfully', { module: 'AssignToMeButtonComponent' });
			})
			.catch((error) => {
				this.assignmentStatus.set(AssignmentStatus.UnAssigned);
				this.timberService.errorReportEvent({
					object: EvenlyCloneStage.Failed,
					code: '',
					severity: BiSeverity.Warning,
					description: `${orderId}, Error: ${error.message}`,
				});
				this.timberService.error('Error while assigning order', { module: 'AssignToMeButtonComponent', error: error });
			});
	}

	private getRxAssignmentStatusAndUpdateButton(triggerSpinner: boolean): Observable<number> {
		return this.rxService.getReferredOrderRxByOrderId(Number(this.orderId), triggerSpinner).pipe(
			map((response) => response.Result.AssignmentStatus as number),
			tap((assignmentStatus) => {
				this.assignmentStatus.set(assignmentStatus);
			})
		);
	}
}
